/**
 * Figma Node IDs:
 * Design Frame: 385:19706
 * Instance IDs: I1497:75191, I1497:76407, I1497:76682, I1497:77380, I385:19709, I1497:75904
 * Composite: true
 * Last Updated: 2025-01-15
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import { useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useIntl, defineMessages } from "react-intl";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";

import { CircleX } from "@labxchange/icons";

import { LxButton } from "../LxButton";
import { ButtonType } from "../LxButton/constants";
import { fetchTranslations } from "./locales/config";
import * as styles from "./Modal.css";

const messages = defineMessages({
  close: {
    id: "modal.close",
    defaultMessage: "Close",
    description: "Close button text for modal",
  },
});

export type ModalSize = "small" | "medium" | "large";

export interface ModalProps {
  /** Content to render inside the modal */
  children: React.ReactNode;

  /** Controls whether the modal is open (controlled mode). If not provided, modal manages its own state. */
  isOpen?: boolean;
  /** Optional callback fired before the modal closes */
  onClose?: () => void;
  /** Additional CSS class for the modal container */
  className?: string;
  /** Test identifier for automated testing */
  testId?: string;
  /** Accessible label for the modal dialog */
  ariaLabel?: string;
  /** ID of element describing the modal content */
  ariaDescribedBy?: string;

  /** Custom translation message ID for close button text (default: "modal.close") */
  closeButtonText?: string;
  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;
  /** Size variant controlling max-width (default: "medium") */
  size?: ModalSize;
  /** Render modal inline without portal (for documentation/preview purposes) */
  inline?: boolean;
  /** Variant for the close button (default: "oncolor") */
  closeButtonVariant?: ButtonType;
}

const ModalInternal: React.FC<ModalProps> = ({
  children,
  isOpen: isOpenProp,
  onClose,
  className,
  testId,
  ariaLabel,
  ariaDescribedBy,
  closeButtonText,
  showCloseButton = true,
  size = "medium",
  inline = false,
  closeButtonVariant = "oncolor",
}) => {
  const intl = useIntl();
  const isControlled = isOpenProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = isControlled ? isOpenProp : internalOpen;

  const handleClose = useCallback(() => {
    onClose?.();
    if (!isControlled) {
      setInternalOpen(false);
    }
  }, [onClose, isControlled]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose]
  );

  const combinedClassName = [
    styles.modalContainer,
    styles.sizeVariants[size],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const closeButtonEl = showCloseButton ? (
    <div className={styles.closeButtonContainer}>
      <LxButton
        variant={closeButtonVariant}
        size="xs"
        text={
          closeButtonText
            ? intl.formatMessage({
                id: closeButtonText,
                defaultMessage: "Close",
                description: "Custom close button text for modal",
              })
            : intl.formatMessage(messages.close)
        }
        Icon={CircleX}
        iconPosition="end"
        onClickHandler={handleClose}
        testId="modal-close-btn"
      />
    </div>
  ) : null;

  if (inline) {
    if (!isOpen) return null;
    return (
      <div
        className={styles.inlineContainer}
        data-testid={testId ? `${testId}-container` : undefined}
      >
        <div
          className={combinedClassName}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
        >
          {closeButtonEl}
          <div className={styles.contentContainer}>{children}</div>
        </div>
      </div>
    );
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={styles.overlay}
          data-testid={testId ? `${testId}-overlay` : undefined}
        >
          <Dialog.Content
            className={combinedClassName}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-modal="true"
            data-testid={testId}
          >
            {closeButtonEl}
            <div className={styles.contentContainer}>{children}</div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const Modal: React.FC<ModalProps> = (props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <ModalInternal {...props} />
  </TranslationsProvider>
);

export default Modal;
